import { useEffect, useRef, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
    Camera, Keyboard, X, ArrowsClockwise,
} from "@phosphor-icons/react";

/**
 * Universal scanner dialog. Supports:
 *  1. Hardware scanners (USB / Bluetooth) — they emit keystrokes very fast
 *     terminated by Enter. We listen for typing-burst-then-Enter on
 *     document.keydown while open.
 *  2. Camera scanners — lazy-loads html5-qrcode and reads QR + 1D barcodes
 *     directly from the device camera.
 *
 * Props:
 *   open: bool
 *   onClose(): void
 *   onCode(code: string, source: "hw"|"cam"): void
 */
export default function ScannerDialog({ open, onClose, onCode }) {
    const [mode, setMode] = useState("idle");      // idle | camera
    const [cameraReady, setCameraReady] = useState(false);
    const [error, setError] = useState("");
    const bufferRef = useRef("");
    const lastKeyAtRef = useRef(0);
    const scannerRef = useRef(null); // html5-qrcode instance
    const regionId = "jw-scanner-region";

    // -------- Hardware scanner: keystroke buffer
    useEffect(() => {
        if (!open) {
            bufferRef.current = "";
            return;
        }
        const onKey = (e) => {
            // Ignore typing inside text inputs (the cashier might be editing
            // something) — except when the user has opted into the scanner
            // dialog and no specific input is focused inside it.
            const target = e.target;
            const insideDialog = target?.closest?.('[role="dialog"]');
            if (!insideDialog) {
                // Outside the dialog DOM, just collect characters
            }
            const now = Date.now();
            // If too long since last key, reset buffer (probably a human typing).
            if (now - lastKeyAtRef.current > 80) bufferRef.current = "";
            lastKeyAtRef.current = now;

            if (e.key === "Enter") {
                const code = bufferRef.current.trim();
                bufferRef.current = "";
                if (code.length >= 3) {
                    e.preventDefault();
                    onCode(code, "hw");
                }
                return;
            }
            if (e.key.length === 1) {
                bufferRef.current += e.key;
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onCode]);

    // -------- Camera scanner lifecycle
    const startCamera = async (cancelledRef) => {
        try {
            const mod = await import("html5-qrcode");
            if (cancelledRef.current) return;
            const { Html5Qrcode } = mod;
            await new Promise((r) => setTimeout(r, 50));
            if (cancelledRef.current) return;
            const scanner = new Html5Qrcode(regionId);
            scannerRef.current = scanner;
            const config = { fps: 12, qrbox: { width: 260, height: 260 } };
            await scanner.start(
                { facingMode: "environment" },
                config,
                (decodedText) => {
                    if (cancelledRef.current) return;
                    scanner.stop().catch(() => {});
                    onCode(decodedText, "cam");
                },
                () => { /* per-frame failure: ignore */ },
            );
            if (!cancelledRef.current) setCameraReady(true);
        } catch (e) {
            if (!cancelledRef.current) setError(e?.message || "No se pudo abrir la cámara");
        }
    };

    useEffect(() => {
        if (mode !== "camera") return;
        const cancelledRef = { current: false };
        // Defer to a microtask so React doesn't see setState calls as
        // happening inside the effect's synchronous body.
        const t = setTimeout(() => startCamera(cancelledRef), 0);
        return () => {
            clearTimeout(t);
            cancelledRef.current = true;
            const s = scannerRef.current;
            scannerRef.current = null;
            if (s) {
                try { s.stop().catch(() => {}); } catch { /* noop */ }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const close = () => {
        const s = scannerRef.current;
        scannerRef.current = null;
        if (s) {
            try { s.stop().catch(() => {}); } catch { /* noop */ }
        }
        setMode("idle");
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent className="sm:max-w-md" data-testid="scanner-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-black text-2xl">
                        Escanear código
                    </DialogTitle>
                </DialogHeader>

                {mode === "idle" && (
                    <div className="space-y-3">
                        <div className="p-4 rounded-2xl bg-emerald-500/10 border-2 border-emerald-500/40 flex items-center gap-3">
                            <Keyboard size={28} weight="fill" className="text-emerald-600 shrink-0" />
                            <div>
                                <p className="font-bold text-sm">Escáner USB/Bluetooth listo</p>
                                <p className="text-xs text-muted-foreground">
                                    Apunta y dispara · el código se lee automáticamente
                                </p>
                            </div>
                            <span className="ml-auto inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                        </div>

                        <button
                            type="button"
                            data-testid="scanner-camera"
                            onClick={() => { setError(""); setCameraReady(false); setMode("camera"); }}
                            className="w-full p-4 rounded-2xl bg-white border-2 border-border hover:border-primary hover:bg-primary/5 transition flex items-center gap-3 text-left"
                        >
                            <Camera size={28} weight="fill" className="text-blue-600 shrink-0" />
                            <div>
                                <p className="font-bold text-sm">Usar cámara del dispositivo</p>
                                <p className="text-xs text-muted-foreground">
                                    QR · EAN · UPC · Code 128 · funciona en celular y tablet
                                </p>
                            </div>
                        </button>

                        <Button
                            variant="outline"
                            onClick={close}
                            className="w-full rounded-full"
                            data-testid="scanner-cancel"
                        >
                            <X size={14} weight="bold" className="mr-1" /> Cerrar
                        </Button>
                    </div>
                )}

                {mode === "camera" && (
                    <div className="space-y-3">
                        <div
                            id={regionId}
                            className="rounded-2xl overflow-hidden bg-black aspect-square w-full grid place-items-center"
                            data-testid="scanner-camera-region"
                        >
                            {!cameraReady && !error && (
                                <ArrowsClockwise size={32} weight="bold" className="text-white animate-spin-slow" />
                            )}
                            {error && (
                                <div className="text-center text-white p-6">
                                    <p className="font-bold">No se pudo abrir la cámara</p>
                                    <p className="text-xs mt-2 opacity-80">{error}</p>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-center text-muted-foreground">
                            Coloca el código dentro del recuadro · se lee automáticamente
                        </p>
                        <Button
                            variant="outline"
                            onClick={close}
                            className="w-full rounded-full"
                            data-testid="scanner-camera-close"
                        >
                            <X size={14} weight="bold" className="mr-1" /> Cancelar
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
